import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { t } from 'i18next';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { useEntitlements } from '@/context_providers/user_context/UserProvider';
import { StringKeys } from '@/resources/strings/StringKeys';
import EmergencyConfigTable from './EmergencyConfigTable';
import { CreateEmergencyConfigModal } from './modals/CreateEmergencyConfigModal';
import { EditEmergencyConfigModal } from './modals/EditEmergencyConfigModal';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import { useEmergencyConfigOperations } from '@/hooks/emergency_config_hooks/useEmergencyConfigOperations';
import type {
  EmergencyConfigurationOnAzure,
  AddEmergencyConfigRequest,
  UpdateEmergencyConfigRequest,
} from '@/../generated-client/models';

const EmergencyConfigurationsTab: React.FC = () => {
  const { organization } = useOrganizationUpdaterProvider();
  const entitlements = useEntitlements();
  const rowsLocked = !entitlements.orgHasRespondInventory;
  const [configs, setConfigs] = useState<EmergencyConfigurationOnAzure[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [configToEdit, setConfigToEdit] = useState<EmergencyConfigurationOnAzure | null>(null);
  const [configToRemove, setConfigToRemove] = useState<EmergencyConfigurationOnAzure | null>(null);
  const [configToHighlight, setConfigToHighlight] = useState<string | null>(null);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');

  const { createConfig, updateConfig, deleteConfig, fetchConfigs } = useEmergencyConfigOperations({
    updateLocalState: (op, c) => {
      if (op === 'add') setConfigs((prev) => [...prev, c]);
      else if (op === 'update') setConfigs((prev) => prev.map((x) => (x.id === c.id ? c : x)));
      else if (op === 'delete') setConfigs((prev) => prev.filter((x) => x.id !== c.id));
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!organization?.org_code) return;
      setLoadingConfigs(true);
      const result = await fetchConfigs(organization.org_code);
      if (!cancelled && result) setConfigs(result);
      if (!cancelled) setLoadingConfigs(false);
    })();
    return () => { cancelled = true; };
  }, [organization?.org_code]);

  const handleCreate = useCallback(async (req: AddEmergencyConfigRequest) => {
    if (!organization?.org_code) return;
    const result = await createConfig({ ...req, org_code: organization.org_code });
    if (result) {
      setShowCreateModal(false);
      setConfigToHighlight(result.spec_name);
      setTimeout(() => setConfigToHighlight(null), 3000);
    }
  }, [organization, createConfig]);

  const handleUpdate = useCallback(async (req: UpdateEmergencyConfigRequest) => {
    const result = await updateConfig(req);
    if (result) {
      setShowEditModal(false);
      setConfigToEdit(null);
      setConfigToHighlight(result.spec_name);
      setTimeout(() => setConfigToHighlight(null), 3000);
    }
  }, [updateConfig]);

  const handleDelete = useCallback(async () => {
    if (!configToRemove) return;
    const success = await deleteConfig(configToRemove);
    if (success) {
      setShowConfirmModal(false);
      setConfigToRemove(null);
    }
  }, [configToRemove, deleteConfig]);

  if (loadingConfigs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {rowsLocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(StringKeys.CONFIG_REQUIRES_RESPOND)}
        </Alert>
      )}
      <EmergencyConfigTable
        setShowCreateConfigurationModal={() => setShowCreateModal(true)}
        editConfiguration={setConfigToEdit}
        showConfigurationToEditModal={() => setShowEditModal(true)}
        setConfirmMessage={setConfirmMessage}
        setConfirmTitle={setConfirmTitle}
        setShowConfirmModal={setShowConfirmModal}
        setConfigurationToRemove={setConfigToRemove}
        configToHighlight={configToHighlight}
        specificationResponseData={configs}
        rowsLocked={rowsLocked}
      />
      {showCreateModal && (
        <CreateEmergencyConfigModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          onCreate={handleCreate}
          locked={rowsLocked}
        />
      )}
      {showEditModal && configToEdit && (
        <EditEmergencyConfigModal
          show={showEditModal}
          onHide={() => { setShowEditModal(false); setConfigToEdit(null); }}
          config={configToEdit}
          onUpdate={handleUpdate}
        />
      )}
      <ConfirmModal
        show={showConfirmModal}
        confirm={handleDelete}
        confirmTitle={confirmTitle}
        confirmMessage={confirmMessage}
        onHide={() => { setShowConfirmModal(false); setConfigToRemove(null); }}
      />
    </Box>
  );
};

export default EmergencyConfigurationsTab;
